<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ImockupBottomTimeline from '@/components/imockup/ImockupBottomTimeline.vue'
import ImockupLeftRail from '@/components/imockup/ImockupLeftRail.vue'
import ImockupPanelSplitter from '@/components/imockup/ImockupPanelSplitter.vue'
import ImockupRightRail from '@/components/imockup/ImockupRightRail.vue'
import ImockupStage from '@/components/imockup/ImockupStage.vue'
import ImockupLayerToast from '@/components/imockup/ImockupLayerToast.vue'
import ImockupTopNav from '@/components/imockup/ImockupTopNav.vue'
import {
  IMOCKUP_LAYOUT,
  bottomTimelineMaxPx,
  initialShellPanelPixels,
  sideRailsMaxCombinedPx,
} from '@/config/imockupLayout'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { useEditorAuthDialog } from '@/composables/useEditorAuthDialog'
import { useEditorTemplateDeepLink } from '@/composables/useEditorTemplateDeepLink'
import {
  clearEditorDraft,
  readEditorDraftAsync,
  type EditorDraftAssetRecord,
} from '@/project/editorProjectDraftCache'
import type { ProjectSnapshot } from '@/project/projectSnapshot'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const { t } = useI18n()
const { isLoggedIn } = useEditorAuth()
const { openEditorAuthDialog } = useEditorAuthDialog()
const { tryConsumeTemplateParam } = useEditorTemplateDeepLink()

/** 未登录时阻断画布与时间轴等编辑区交互 */
const editorInteractionBlocked = computed(() => !isLoggedIn.value)

const SPLITTER_V = IMOCKUP_LAYOUT.splitterVertical
const MIN_LEFT = IMOCKUP_LAYOUT.minLeftRail
const MIN_RIGHT = IMOCKUP_LAYOUT.minRightRail
const MIN_CENTER = IMOCKUP_LAYOUT.minCenterStage
const MIN_BOTTOM = IMOCKUP_LAYOUT.minBottomTimeline

const shellRef = ref<HTMLElement | null>(null)
const store = useMockupEditorStore()
const { workspaceMode } = storeToRefs(store)

const draftRestoreOpen = ref(false)
const draftSavedAt = ref<number | null>(null)
const draftSnapshot = shallowRef<ProjectSnapshot | null>(null)
const draftAssets = shallowRef<Map<string, EditorDraftAssetRecord>>(new Map())
let editorDraftGateResolved = false
let draftEntryInFlight = false

function formatDraftSavedAt(ms: number): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(ms),
    )
  } catch {
    return new Date(ms).toLocaleString()
  }
}

async function runPostLoginEntry(): Promise<void> {
  if (!isLoggedIn.value || editorDraftGateResolved || draftEntryInFlight) return
  draftEntryInFlight = true
  try {
    await tryConsumeTemplateParam()
    const draft = await readEditorDraftAsync()
    if (draft) {
      draftSnapshot.value = draft.snapshot
      draftSavedAt.value = draft.savedAt
      draftAssets.value = draft.assets
      draftRestoreOpen.value = true
    } else {
      store.enableEditorDraftPersistence()
      editorDraftGateResolved = true
    }
  } finally {
    draftEntryInFlight = false
  }
}

async function onRestoreEditorDraft(): Promise<void> {
  const snap = draftSnapshot.value
  if (!snap) return
  await store.restoreEditorFromDraft(snap, draftAssets.value)
  draftRestoreOpen.value = false
  draftSnapshot.value = null
  draftSavedAt.value = null
  draftAssets.value = new Map()
  store.enableEditorDraftPersistence()
  editorDraftGateResolved = true
}

function onDiscardEditorDraft(): void {
  clearEditorDraft()
  draftRestoreOpen.value = false
  draftSnapshot.value = null
  draftSavedAt.value = null
  draftAssets.value = new Map()
  store.enableEditorDraftPersistence()
  editorDraftGateResolved = true
}

const initialPanels = initialShellPanelPixels()
const leftRailPx = ref<number>(initialPanels.leftRailPx)
const rightRailPx = ref<number>(initialPanels.rightRailPx)
const bottomTimelinePx = ref<number>(initialPanels.bottomTimelinePx)

let ro: ResizeObserver | null = null

function shellRect(): DOMRect {
  return shellRef.value?.getBoundingClientRect() ?? new DOMRect(0, 0, 1200, 800)
}

/**
 * 窗口或壳层尺寸变化时收紧面板像素，避免中间区域被挤没。
 */
function clampLayout(): void {
  const r = shellRect()
  const sw = r.width
  const gap = SPLITTER_V * 2

  let left = leftRailPx.value
  let right = rightRailPx.value

  const maxLeft = sw - right - gap - MIN_CENTER
  left = Math.max(MIN_LEFT, Math.min(maxLeft, left))

  const maxRight = sw - left - gap - MIN_CENTER
  right = Math.max(MIN_RIGHT, Math.min(maxRight, right))

  left = Math.max(MIN_LEFT, Math.min(sw - right - gap - MIN_CENTER, left))
  right = Math.max(MIN_RIGHT, Math.min(sw - left - gap - MIN_CENTER, right))

  const capSum = sideRailsMaxCombinedPx(sw)
  let combined = left + right
  if (combined > capSum) {
    const total = combined
    let nl = Math.round((left / total) * capSum)
    let nr = Math.round((right / total) * capSum)
    nl = Math.max(MIN_LEFT, Math.min(nl, capSum - MIN_RIGHT))
    nr = Math.max(MIN_RIGHT, Math.min(nr, capSum - MIN_LEFT))
    while (nl + nr > capSum) {
      if (nl > MIN_LEFT) nl--
      else if (nr > MIN_RIGHT) nr--
      else break
    }
    left = nl
    right = nr
    left = Math.max(MIN_LEFT, Math.min(sw - right - gap - MIN_CENTER, left))
    right = Math.max(MIN_RIGHT, Math.min(sw - left - gap - MIN_CENTER, right))
  }

  leftRailPx.value = Math.round(left)
  rightRailPx.value = Math.round(right)

  const shellH = r.height
  const maxBottom = bottomTimelineMaxPx(shellH, sw)
  bottomTimelinePx.value = Math.max(MIN_BOTTOM, Math.min(maxBottom, bottomTimelinePx.value))
}

function attachResizeObserver(): void {
  if (!shellRef.value) return
  ro = new ResizeObserver(() => {
    clampLayout()
  })
  ro.observe(shellRef.value)
}

/** 拖动左–中分割条：调整左侧机型栏宽度。 */
function onLeftResizeStart(ev: PointerEvent): void {
  if (ev.button !== 0) return
  const shellEl = shellRef.value
  if (!shellEl) return
  const target = ev.currentTarget as HTMLElement
  target.setPointerCapture(ev.pointerId)
  const startX = ev.clientX
  const startLeft = leftRailPx.value
  document.body.classList.add('im-resizing', 'im-resizing--ew')
  let moveRaf = 0
  let lastClientX = ev.clientX

  function applyLeftFromPointer(): void {
    const box = shellRef.value
    if (!box) return
    const sw = box.getBoundingClientRect().width
    const dx = lastClientX - startX
    const capSum = sideRailsMaxCombinedPx(sw)
    const maxLeft = Math.min(
      sw - rightRailPx.value - SPLITTER_V * 2 - MIN_CENTER,
      capSum - rightRailPx.value,
    )
    leftRailPx.value = Math.round(Math.max(MIN_LEFT, Math.min(maxLeft, startLeft + dx)))
  }

  function move(e: PointerEvent): void {
    lastClientX = e.clientX
    if (moveRaf) return
    moveRaf = window.requestAnimationFrame(() => {
      moveRaf = 0
      applyLeftFromPointer()
    })
  }

  function up(e: PointerEvent): void {
    try {
      target.releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    if (moveRaf) {
      window.cancelAnimationFrame(moveRaf)
      moveRaf = 0
    }
    applyLeftFromPointer()
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
    document.body.classList.remove('im-resizing', 'im-resizing--ew')
    clampLayout()
  }

  window.addEventListener('pointermove', move, { passive: true })
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
}

/** 拖动中–右分割条：调整右侧属性栏宽度。 */
function onRightResizeStart(ev: PointerEvent): void {
  if (ev.button !== 0) return
  const shellEl = shellRef.value
  if (!shellEl) return
  const target = ev.currentTarget as HTMLElement
  target.setPointerCapture(ev.pointerId)
  const startX = ev.clientX
  const startRight = rightRailPx.value
  document.body.classList.add('im-resizing', 'im-resizing--ew')
  let moveRaf = 0
  let lastClientX = ev.clientX

  function applyRightFromPointer(): void {
    const box = shellRef.value
    if (!box) return
    const sw = box.getBoundingClientRect().width
    const capSum = sideRailsMaxCombinedPx(sw)
    const maxRight = Math.min(
      sw - leftRailPx.value - SPLITTER_V * 2 - MIN_CENTER,
      capSum - leftRailPx.value,
    )
    const dx = lastClientX - startX
    rightRailPx.value = Math.round(Math.max(MIN_RIGHT, Math.min(maxRight, startRight - dx)))
  }

  function move(e: PointerEvent): void {
    lastClientX = e.clientX
    if (moveRaf) return
    moveRaf = window.requestAnimationFrame(() => {
      moveRaf = 0
      applyRightFromPointer()
    })
  }

  function up(e: PointerEvent): void {
    try {
      target.releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    if (moveRaf) {
      window.cancelAnimationFrame(moveRaf)
      moveRaf = 0
    }
    applyRightFromPointer()
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
    document.body.classList.remove('im-resizing', 'im-resizing--ew')
    clampLayout()
  }

  window.addEventListener('pointermove', move, { passive: true })
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
}

/** 拖动画布–时间轴分割条：调整底部动画区高度。 */
function onBottomResizeStart(ev: PointerEvent): void {
  if (ev.button !== 0) return
  const shellEl = shellRef.value
  if (!shellEl) return
  const target = ev.currentTarget as HTMLElement
  target.setPointerCapture(ev.pointerId)
  const startY = ev.clientY
  const startBottom = bottomTimelinePx.value
  document.body.classList.add('im-resizing', 'im-resizing--ns')
  let moveRaf = 0
  let lastClientY = ev.clientY

  function applyBottomFromPointer(): void {
    const box = shellRef.value
    if (!box) return
    const shellH = box.getBoundingClientRect().height
    const shellW = box.getBoundingClientRect().width
    const dy = lastClientY - startY
    const maxBottom = bottomTimelineMaxPx(shellH, shellW)
    // 向下拖 splitter 增大画布区、减小时间轴（与常见 IDE 分割条一致）。
    bottomTimelinePx.value = Math.round(Math.max(MIN_BOTTOM, Math.min(maxBottom, startBottom - dy)))
  }

  function move(e: PointerEvent): void {
    lastClientY = e.clientY
    if (moveRaf) return
    moveRaf = window.requestAnimationFrame(() => {
      moveRaf = 0
      applyBottomFromPointer()
    })
  }

  function up(e: PointerEvent): void {
    try {
      target.releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    if (moveRaf) {
      window.cancelAnimationFrame(moveRaf)
      moveRaf = 0
    }
    applyBottomFromPointer()
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
    document.body.classList.remove('im-resizing', 'im-resizing--ns')
    clampLayout()
  }

  window.addEventListener('pointermove', move, { passive: true })
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
}

onMounted(() => {
  clampLayout()
  attachResizeObserver()
  if (isLoggedIn.value) {
    void runPostLoginEntry()
  } else {
    openEditorAuthDialog('login')
  }
})

watch(isLoggedIn, (loggedIn) => {
  if (loggedIn) void runPostLoginEntry()
})

onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
})
</script>

<template>
  <div class="page">
    <ImockupTopNav />

    <div class="shellWrap" :class="{ 'shellWrap--blocked': editorInteractionBlocked }">
      <div
        ref="shellRef"
        class="shell"
        :class="{ 'shell--edit': workspaceMode === 'edit' }"
        :style="{
          '--panel-left': `${leftRailPx}px`,
          '--panel-right': `${rightRailPx}px`,
          '--panel-bottom': `${bottomTimelinePx}px`,
        }"
      >
      <ImockupLeftRail
        class="shellSide shellSide--leftTop"
        :class="{ 'is-collapsed': workspaceMode === 'edit' }"
      />
      <ImockupPanelSplitter
        class="shellSplit shellSplit--left"
        :class="{ 'is-collapsed': workspaceMode === 'edit' }"
        orientation="vertical"
        @start="onLeftResizeStart"
      />
      <ImockupStage class="shellMain shellMain--stage" />
      <ImockupPanelSplitter
        class="shellSplit shellSplit--btm"
        orientation="horizontal"
        @start="onBottomResizeStart"
      />
      <ImockupBottomTimeline class="shellMain shellMain--timeline" />
      <ImockupPanelSplitter
        class="shellSplit shellSplit--right"
        orientation="vertical"
        @start="onRightResizeStart"
      />
      <ImockupRightRail class="shellSide shellSide--right" />
      </div>

      <div
        v-if="editorInteractionBlocked"
        class="authGate"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-auth-gate-title"
      >
        <div class="authGate__panel">
          <h2 id="editor-auth-gate-title" class="authGate__title">{{ t('gate.title') }}</h2>
          <p class="authGate__hint">{{ t('gate.hint') }}</p>
        </div>
      </div>
    </div>

    <!-- <ImockupStatusBar /> -->
    <!-- <ImockupFooterBar /> -->

    <ImockupLayerToast />

    <div
      v-if="draftRestoreOpen"
      class="draftRestore"
      role="dialog"
      aria-modal="true"
      aria-labelledby="editor-draft-restore-title"
    >
      <div class="draftRestore__panel">
        <h2 id="editor-draft-restore-title" class="draftRestore__title">{{ t('editorDraft.dialogTitle') }}</h2>
        <p class="draftRestore__hint">{{ t('editorDraft.dialogHint') }}</p>
        <p v-if="draftSavedAt != null" class="draftRestore__time">
          {{ t('editorDraft.timeHint', { time: formatDraftSavedAt(draftSavedAt) }) }}
        </p>
        <div class="draftRestore__actions">
          <button type="button" class="draftRestore__btn draftRestore__btn--primary" @click="onRestoreEditorDraft">
            {{ t('editorDraft.restore') }}
          </button>
          <button type="button" class="draftRestore__btn" @click="onDiscardEditorDraft">
            {{ t('editorDraft.discard') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page {
  min-height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--im-bg);
  color: var(--im-text);
  overflow: hidden;
}

.shellWrap {
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.shellWrap--blocked .shell {
  pointer-events: none;
  user-select: none;
}

.authGate {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  background: rgb(15 23 42 / 42%);
  pointer-events: auto;
}

.authGate__panel {
  max-width: 26rem;
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  background: var(--im-bg-elevated, var(--im-bg));
  border: 1px solid var(--im-border, rgb(148 163 184 / 35%));
  box-shadow: 0 18px 48px rgb(15 23 42 / 28%);
}

.authGate__title {
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 650;
  line-height: 1.35;
}

.authGate__hint {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--im-text-muted, rgb(100 116 139));
}

.draftRestore {
  position: fixed;
  inset: 0;
  z-index: 48;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  background: rgb(15 23 42 / 48%);
  pointer-events: auto;
}

.draftRestore__panel {
  max-width: 26rem;
  width: min(100%, 26rem);
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  background: var(--im-bg-elevated, var(--im-bg));
  border: 1px solid var(--im-border, rgb(148 163 184 / 35%));
  box-shadow: 0 18px 48px rgb(15 23 42 / 28%);
}

.draftRestore__title {
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 650;
  line-height: 1.35;
}

.draftRestore__hint,
.draftRestore__time {
  margin: 0 0 0.65rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--im-text-muted, rgb(100 116 139));
}

.draftRestore__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.draftRestore__btn {
  appearance: none;
  border-radius: 0.5rem;
  border: 1px solid var(--im-border, rgb(148 163 184 / 45%));
  padding: 0.45rem 0.85rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: inherit;
}

.draftRestore__btn--primary {
  border-color: transparent;
  background: rgb(59 130 246);
  color: #fff;
}

.shell {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: grid;
  grid-template-columns: var(--panel-left, 380px) 10px minmax(0, 1fr) 10px var(--panel-right, 400px);
  grid-template-rows: minmax(0, 1fr) 10px var(--panel-bottom, 360px);
  align-items: stretch;
  transition: grid-template-columns 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.shell.shell--edit {
  grid-template-columns: 0px 0px minmax(0, 1fr) 10px var(--panel-right, 400px);
}

.shellSide {
  min-width: 0;
  min-height: 0;
}

.shellMain {
  min-width: 0;
  min-height: 0;
}

.shellSide--leftTop,
.shellSplit--left {
  transition:
    opacity 220ms ease-out,
    transform 220ms ease-out;
}

.shellSide--leftTop {
  grid-column: 1;
  grid-row: 1;
}

.shellMain--stage {
  grid-column: 3;
  grid-row: 1;
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.shellMain--timeline {
  grid-column: 1 / 4;
  grid-row: 3;
}

.shellSide--right {
  grid-column: 5;
  grid-row: 1 / 4;
}

.shellSplit--left {
  grid-column: 2;
  grid-row: 1;
}

.shellSide--leftTop.is-collapsed,
.shellSplit--left.is-collapsed {
  opacity: 0;
  transform: translateX(-10px);
  pointer-events: none;
}

.shellSplit--btm {
  grid-column: 1 / 4;
  grid-row: 2;
}

.shellSplit--right {
  grid-column: 4;
  grid-row: 1 / 4;
}

.shell.shell--edit .shellMain--stage {
  transform: translateX(-2px);
}

@media (prefers-reduced-motion: reduce) {
  .shell,
  .shellSide--leftTop,
  .shellSplit--left,
  .shellMain--stage {
    transition: none;
  }

  .shell.shell--edit .shellMain--stage {
    transform: none;
  }
}

@media (max-width: 900px) {
  .shell {
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .shellSplit {
    display: none;
  }

  .shellSide--leftTop,
  .shellMain--stage,
  .shellMain--timeline,
  .shellSide--right {
    grid-column: auto;
    grid-row: auto;
  }

  .shellMain--stage {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: min(58vh, 640px);
  }

  .shellMain--timeline {
    min-height: min(40vh, 420px);
  }
}
</style>

<style>
/** 拖动分割条时锁定光标，避免选中文本。 */
body.im-resizing {
  user-select: none;
  cursor: default;
}

body.im-resizing.im-resizing--ew {
  cursor: ew-resize;
}

body.im-resizing.im-resizing--ns {
  cursor: ns-resize;
}
</style>
